import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { Finish, CreateFinish, UpdateFinish } from '@estimator/shared';

export function useFinishes() {
  return useQuery({
    queryKey: ['finishes'],
    queryFn: async () => {
      const { data } = await apiClient.get<Finish[]>('/finishes');
      return data;
    },
  });
}

export function useFinish(id: string | null) {
  return useQuery({
    queryKey: ['finishes', id],
    queryFn: async () => {
      const { data } = await apiClient.get<Finish>(`/finishes/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateFinish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFinish) => {
      const { data } = await apiClient.post<Finish>('/finishes', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finishes'] }),
  });
}

export function useUpdateFinish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateFinish & { id: string }) => {
      const { data } = await apiClient.patch<Finish>(`/finishes/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['finishes'] });
      queryClient.invalidateQueries({ queryKey: ['finishes', vars.id] });
    },
  });
}

export function useDeleteFinish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/finishes/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finishes'] }),
  });
}
